import React, { createContext, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { logoutRequest } from "../controllers/authService";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  // (2026-05-18 A8) id del timer de expiración para poder limpiarlo (evita
  // que un timer viejo cierre una sesión nueva en re-login/desmontaje).
  const expTimerRef = useRef(null);

  useEffect(() => {
    const storedUser = localStorage.getItem("authData");

    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);

      // Token ya expirado
      if (Date.now() >= parsedUser.expiresAt) {
        handleLogout(true);
      } else {
        setUser(parsedUser);
        startExpirationTimer(parsedUser.expiresAt);
      }
    }

    setLoading(false);

    // (2026-05-18 A8) Limpia el timer al desmontar el provider.
    return () => {
      if (expTimerRef.current) clearTimeout(expTimerRef.current);
    };
  }, []);

  const startExpirationTimer = (expiresAt) => {
    // (2026-05-18 A8) Limpia cualquier timer previo (re-login en la misma
    // pestaña) y acota el delay al máximo seguro de setTimeout (~24.8 días):
    // un valor mayor desborda a 32-bit y dispararía el logout de inmediato.
    if (expTimerRef.current) clearTimeout(expTimerRef.current);

    const MAX_DELAY = 2147483647; // 2^31 - 1 ms
    let timeLeft = expiresAt - Date.now();
    if (timeLeft < 0) timeLeft = 0;
    if (timeLeft > MAX_DELAY) timeLeft = MAX_DELAY;

    expTimerRef.current = setTimeout(() => {
      handleLogout(true);
    }, timeLeft);
  };

  const login = (data) => {
    window._sessionExpired = false;
    // (2026-05-18) Al ENTRAR a la plataforma el menú lateral debe mostrarse
    // desde el principio: se descarta cualquier posición de scroll previa.
    sessionStorage.removeItem("qi_sidebar_scroll");
    // (2026-07-05) El banner de vencimiento de contraseña es "descartable por
    // sesión pero reaparece en cada ingreso": se limpia la marca al loguear.
    sessionStorage.removeItem("pwd_banner_dismissed");

    const expiresAt = Date.now() + data.expires_in * 1000;

    const authData = {
      ...data,
      expiresAt,
    };

    setUser(authData);
    localStorage.setItem("authData", JSON.stringify(authData));
  };

  const handleLogout = (expired = false) => {
    // (2026-05-18 A8) Cancela el timer de expiración pendiente.
    if (expTimerRef.current) {
      clearTimeout(expTimerRef.current);
      expTimerRef.current = null;
    }
    // (2026-05-20) Single-session option #2: notificar al backend que
    // limpie usuarios.current_jti. Best-effort: no esperamos error.
    // Si la sesion ya expiro (expired=true) o fue invalidada por otro
    // dispositivo, el endpoint devuelve 401 -> simplemente ignoramos.
    // No usamos await para no demorar el redirect; la promesa corre en
    // background.
    if (!expired) {
      try { logoutRequest(); } catch (_) { /* no-op */ }
    }
    setUser(null);
    localStorage.removeItem("authData");
    sessionStorage.removeItem("qi_sidebar_scroll");

    if (expired) {
      toast.warning("Tu sesión ha expirado. Inicia sesión nuevamente.");
    }

    navigate("/login");
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout: handleLogout,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
